/**
 * Phase 322b — 4-Step Academy Onboarding Wizard.
 *
 * Force-rendered for the new owner immediately after they exchange the
 * magic link. Drives 4 micro-conversions in sequence:
 *   1. Brand     — logo upload → server-side colorthief → primary hex saved.
 *   2. Finance   — default monthly fee per student.
 *   3. Geofence  — Leaflet+OSM pin drop on the training centre.
 *   4. Unboxing  — canvas-confetti explosion + "Enter Dashboard" CTA.
 *
 * Step state lives entirely in URL hash so a hard refresh resumes where
 * the owner left off. Backend writes are idempotent — re-saving a step
 * is fine.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Palette, Wallet, MapPin, PartyPopper, Loader2, ChevronRight, ChevronLeft, CheckCircle2, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import api from '@/lib/api';

// Leaflet's default marker icons need explicit URLs in bundled apps.
// Use the standard CDN-hosted assets so we don't bloat the bundle.
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STEPS = [
  { id: 'brand',    icon: Palette,     title: 'Brand identity',  blurb: 'Upload your academy logo' },
  { id: 'finance',  icon: Wallet,      title: 'Financial baseline', blurb: 'Default monthly fee per student' },
  { id: 'geofence', icon: MapPin,      title: 'Spatial geofence', blurb: 'Drop a pin on your turf' },
  { id: 'unboxing', icon: PartyPopper, title: 'You\u2019re live!', blurb: 'Your Zero-Touch OS is ready' },
];

const DEFAULT_MAP_CENTER = [20.5937, 78.9629]; // India centroid

function PinDropper({ pos, onChange }) {
  useMapEvents({
    click(e) {
      onChange([e.latlng.lat, e.latlng.lng]);
    },
  });
  return pos ? <Marker position={pos} /> : null;
}

export default function AcademyWizard() {
  const navigate = useNavigate();
  const [activeIdx, setActiveIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    logo_url: '',
    primary_hex: '#0F766E',
    default_fee_inr: 2000,
    lat: null,
    lng: null,
    geofence_label: 'Main Centre',
  });
  const fileRef = useRef(null);
  const confettiFiredRef = useRef(false);

  // Hash-driven resume
  useEffect(() => {
    const id = (window.location.hash || '').replace('#', '');
    const idx = STEPS.findIndex((s) => s.id === id);
    if (idx >= 0) setActiveIdx(idx);
  }, []);
  useEffect(() => {
    window.location.hash = STEPS[activeIdx].id;
  }, [activeIdx]);

  // Trigger confetti once we land on the final step.
  useEffect(() => {
    if (STEPS[activeIdx].id !== 'unboxing' || confettiFiredRef.current) return;
    confettiFiredRef.current = true;
    const fire = (opts) => confetti({
      particleCount: 80, spread: 70, origin: { y: 0.6 }, ...opts,
    });
    fire({});
    setTimeout(() => fire({ angle: 60,  spread: 55, origin: { x: 0,   y: 0.7 } }), 250);
    setTimeout(() => fire({ angle: 120, spread: 55, origin: { x: 1,   y: 0.7 } }), 500);
  }, [activeIdx]);

  const goNext = () => setActiveIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const goBack = () => setActiveIdx((i) => Math.max(0, i - 1));

  // ─── Step 1 — Brand ───
  const submitBrand = async (file) => {
    if (!file) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/onboarding/wizard/brand', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setData((d) => ({ ...d, logo_url: r.data.logo_url, primary_hex: r.data.primary_hex }));
      toast.success(`Brand colour detected: ${r.data.primary_hex}`);
      goNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Logo upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step 2 — Finance ───
  const submitFinance = async () => {
    setSubmitting(true);
    try {
      await api.post('/onboarding/wizard/finance', { default_fee_inr: data.default_fee_inr });
      toast.success(`Default fee set to ₹${Number(data.default_fee_inr).toLocaleString('en-IN')}/mo`);
      goNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save fee');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step 3 — Geofence ───
  const submitGeofence = async () => {
    if (data.lat == null || data.lng == null) {
      toast.error('Please drop a pin on the map first');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/onboarding/wizard/geofence', {
        lat: data.lat, lng: data.lng, label: data.geofence_label,
      });
      toast.success('Training centre pinned');
      goNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save location');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step 4 — Unboxing finish ───
  const finishWizard = async () => {
    setSubmitting(true);
    try {
      await api.post('/onboarding/wizard/complete', {});
      navigate('/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete wizard');
      setSubmitting(false);
    }
  };

  const StepIcon = STEPS[activeIdx].icon;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-900 flex items-center justify-center px-4 py-10"
      data-testid="academy-wizard"
    >
      <Card className="max-w-2xl w-full shadow-2xl">
        {/* ─── Header: progress + step counter ─── */}
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Step {activeIdx + 1} of {STEPS.length}
            </span>
            <span className="text-xs text-muted-foreground">{Math.round(((activeIdx + 1) / STEPS.length) * 100)}% complete</span>
          </div>
          <Progress value={((activeIdx + 1) / STEPS.length) * 100} className="h-1.5 mb-4" />
          <CardTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <StepIcon className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <div className="text-lg font-display">{STEPS[activeIdx].title}</div>
              <div className="text-xs font-normal text-muted-foreground">{STEPS[activeIdx].blurb}</div>
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">

          {/* ─── Step 1 — Brand ─── */}
          {STEPS[activeIdx].id === 'brand' && (
            <div className="space-y-4" data-testid="wizard-step-brand">
              <label
                className="block border-2 border-dashed border-emerald-200 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 transition"
                htmlFor="logo-input"
              >
                {data.logo_url ? (
                  <img src={data.logo_url} alt="Logo preview" className="h-24 w-24 object-contain mx-auto rounded-lg shadow" />
                ) : (
                  <Upload className="h-10 w-10 text-emerald-500 mx-auto" />
                )}
                <p className="text-sm font-medium text-slate-700 mt-3">
                  {data.logo_url ? 'Choose a different logo' : 'Upload your academy logo'}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">PNG / JPG / WebP up to 5 MB. We&apos;ll extract your brand colour automatically.</p>
                <input
                  ref={fileRef}
                  id="logo-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => submitBrand(e.target.files?.[0])}
                  data-testid="wizard-logo-input"
                />
              </label>
              {data.primary_hex && (
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <span>Detected primary:</span>
                  <span
                    className="inline-block h-4 w-4 rounded border"
                    style={{ background: data.primary_hex }}
                  />
                  <code className="font-mono text-slate-700">{data.primary_hex}</code>
                </div>
              )}
              <div className="flex justify-between pt-2">
                <span className="text-xs text-muted-foreground italic self-center">No going back — your academy is now branded.</span>
                <Button
                  variant="ghost"
                  onClick={goNext}
                  className="text-emerald-700"
                  data-testid="wizard-brand-skip"
                >
                  Skip for now →
                </Button>
              </div>
            </div>
          )}

          {/* ─── Step 2 — Finance ─── */}
          {STEPS[activeIdx].id === 'finance' && (
            <div className="space-y-4" data-testid="wizard-step-finance">
              <Label htmlFor="fee" className="text-sm">Default monthly fee per student (₹)</Label>
              <Input
                id="fee"
                type="number"
                min="1"
                max="100000"
                value={data.default_fee_inr}
                onChange={(e) => setData((d) => ({ ...d, default_fee_inr: Number(e.target.value) }))}
                className="text-2xl tabular-nums py-6 text-emerald-800 font-display font-semibold"
                data-testid="wizard-fee-input"
              />
              <p className="text-xs text-muted-foreground">
                This becomes your default fee plan. You can add more plans later from the Finance tab — no problem.
              </p>
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={goBack} data-testid="wizard-finance-back">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <Button
                  onClick={submitFinance}
                  disabled={submitting || !data.default_fee_inr}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="wizard-finance-next"
                >
                  {submitting
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <ChevronRight className="h-4 w-4 mr-1" />}
                  Save &amp; continue
                </Button>
              </div>
            </div>
          )}

          {/* ─── Step 3 — Geofence ─── */}
          {STEPS[activeIdx].id === 'geofence' && (
            <div className="space-y-4" data-testid="wizard-step-geofence">
              <p className="text-sm text-slate-700">
                Tap on the map to drop a pin on the centre of your training facility.
                This powers staff geofence check-in and parent maps.
              </p>
              <div className="rounded-xl overflow-hidden border border-emerald-200 shadow" data-testid="wizard-map">
                <MapContainer
                  center={DEFAULT_MAP_CENTER}
                  zoom={5}
                  style={{ height: '320px', width: '100%' }}
                  scrollWheelZoom
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <PinDropper
                    pos={data.lat != null && data.lng != null ? [data.lat, data.lng] : null}
                    onChange={([lat, lng]) => setData((d) => ({ ...d, lat, lng }))}
                  />
                </MapContainer>
              </div>
              {data.lat != null && data.lng != null && (
                <div className="flex items-center gap-2 text-xs text-slate-600 px-1">
                  <MapPin className="h-3.5 w-3.5 text-emerald-600" />
                  <code className="font-mono tabular-nums">{data.lat.toFixed(5)}, {data.lng.toFixed(5)}</code>
                </div>
              )}
              <div>
                <Label htmlFor="venue-label" className="text-xs">Centre name (optional)</Label>
                <Input
                  id="venue-label"
                  value={data.geofence_label}
                  onChange={(e) => setData((d) => ({ ...d, geofence_label: e.target.value }))}
                  placeholder="Main Centre"
                  data-testid="wizard-venue-input"
                />
              </div>
              <div className="flex justify-between pt-1">
                <Button variant="ghost" onClick={goBack} data-testid="wizard-geofence-back">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <Button
                  onClick={submitGeofence}
                  disabled={submitting || data.lat == null}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="wizard-geofence-next"
                >
                  {submitting
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <ChevronRight className="h-4 w-4 mr-1" />}
                  Save &amp; continue
                </Button>
              </div>
            </div>
          )}

          {/* ─── Step 4 — Unboxing ─── */}
          {STEPS[activeIdx].id === 'unboxing' && (
            <div className="text-center space-y-5 py-2" data-testid="wizard-step-unboxing">
              <div className="h-20 w-20 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="h-12 w-12 text-emerald-600" />
              </div>
              <h2 className="text-3xl font-display font-bold text-slate-900">Your Zero-Touch OS is live.</h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
                Brand, fees, location — all set. Your academy is configured and the daily activation
                nudges will skip you (because you&apos;re no longer stuck 🎉).
              </p>

              <ul className="text-left text-xs space-y-2 bg-emerald-50 border border-emerald-200 rounded-lg p-4 max-w-md mx-auto">
                <li className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  Logo + brand colour locked in
                </li>
                <li className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  Default fee of ₹{Number(data.default_fee_inr).toLocaleString('en-IN')}/mo configured
                </li>
                <li className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  Training centre pinned for geofence tracking
                </li>
              </ul>

              <Button
                onClick={finishWizard}
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-6 text-base"
                data-testid="wizard-finish-btn"
              >
                {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <PartyPopper className="h-5 w-5 mr-2" />}
                Enter My Dashboard
              </Button>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  );
}
